import React, { useEffect, useRef, useState } from "react";
import apiClient from "../../../../../api/apiClient";
import { leadDisplayName } from "../shared/crmConstants";

/**
 * Merge a duplicate lead (the same real-world business entered twice) into another lead:
 * citations, activities, conversations and tags move to the target, blank fields are
 * filled from this lead, and this lead is removed.
 */
const MergeLeadModal = ({ lead, onClose, onMerged }) => {
    const [query, setQuery] = useState(lead.company || "");
    const [results, setResults] = useState([]);
    const [target, setTarget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const timer = useRef(null);

    useEffect(() => {
        clearTimeout(timer.current);
        if (query.trim().length < 2) {
            setResults([]);
            return undefined;
        }
        timer.current = setTimeout(() => {
            const params = new URLSearchParams({ search: query.trim(), limit: 10 });
            apiClient
                .get(`/admin/crm/leads?${params}`)
                .then((res) => setResults((res.data.leads || []).filter((l) => l.id !== lead.id)))
                .catch(() => setResults([]));
        }, 300);
        return () => clearTimeout(timer.current);
    }, [query, lead.id]);

    const submit = async () => {
        if (!target) return;
        setSaving(true);
        setError("");
        try {
            const res = await apiClient.post(`/admin/crm/leads/${lead.id}/merge`, { intoLeadId: target.id });
            onMerged(res.data.id);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to merge leads");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="crm-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="crm-modal" style={{ maxWidth: 520 }} role="dialog" aria-modal="true" aria-labelledby="merge-lead-title">
                <div className="crm-modal__header">
                    <h2 id="merge-lead-title" className="crm-modal__title">Merge {leadDisplayName(lead)} into…</h2>
                    <button className="crm-modal__close" onClick={onClose} type="button" aria-label="Close">×</button>
                </div>
                <div className="crm-modal__body">
                    {error && <div className="lf-error">{error}</div>}
                    <label htmlFor="merge-lead-search" className="admin-form-label">Find the lead to keep</label>
                    <input id="merge-lead-search" className="admin-input" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} />
                    <div role="radiogroup" aria-label="Leads" style={{ marginTop: 8 }}>
                        {results.map((r) => (
                            <label key={r.id} className="admin-license-user-chip" style={{ display: "flex", gap: 8, cursor: "pointer" }}>
                                <input type="radio" name="merge-target" checked={target?.id === r.id} onChange={() => setTarget(r)} />
                                <span>
                                    <strong>{leadDisplayName(r)}</strong>{" "}
                                    <span className="admin-license-meta" style={{ display: "inline" }}>
                                        {[r.city, r.state].filter(Boolean).join(", ")} · #{r.id}
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                    {target && (
                        <p className="admin-license-meta" style={{ display: "block" }}>
                            {leadDisplayName(lead)} will be removed; its history moves to {leadDisplayName(target)}.
                        </p>
                    )}
                </div>
                <div className="crm-modal__footer">
                    <button className="admin-btn" onClick={onClose} disabled={saving} type="button">Cancel</button>
                    <button className="admin-btn admin-btn--danger" onClick={submit} disabled={!target || saving} type="button">
                        {saving ? "Merging…" : "Merge"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MergeLeadModal;
